// Intl.NumberFormat construction is expensive (~µs each); constructing one per
// call dominated a ~5.6 s render via sameRounded in the per-receiver diff scan
// (CHO-617 Phase 7 §7E). Hoist the formatters to module singletons and reuse
// them — same output, no per-call construction.
const GRAIN_VALUE_FMT = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });
const NUMBER_INPUT_FMT = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 1,
  useGrouping: false,
});

/**
 * Format a grain-unit measure value. Non-shipping grains can produce fractional
 * values after scalar conversion; per Neal's rule, show the integer when whole,
 * otherwise one decimal place — with thousands separators throughout.
 */
export const formatGrainValue = (n: number | null | undefined): string => {
  if (n == null || !Number.isFinite(n)) return "-";
  return GRAIN_VALUE_FMT.format(n);
};

/**
 * Format for a `<input type="number">` defaultValue — same rounding as display
 * (integer when whole, else 1 decimal) but without grouping separators so the
 * input accepts the value without parse errors. Absorbs floating-point drift
 * from VT back-conversion so users see "800" instead of "799.9999999999".
 */
export const formatForNumberInput = (n: number): string => NUMBER_INPUT_FMT.format(n);

/**
 * Equality at display rounding (1 decimal place, round-half-away-from-zero —
 * matching `maximumFractionDigits: 1`). Scalar conversion at non-shipping grains
 * introduces float noise (e.g. 142.9999… vs 143) that strict === treats as a
 * diff, spuriously showing the "X" and baseline strikethrough on cells the user
 * sees as identical.
 *
 * Compares numerically rather than via string formatting: this runs once per
 * cell in the per-receiver diff scan (rows × periods, ~millions of calls on a
 * cold load), where `Intl`/`toFixed` string work was the single biggest chunk
 * of the post-Arrow render (CHO-617 Phase 7 §7E).
 */
export const sameRounded = (a: number | undefined, b: number | undefined): boolean => {
  if (a === undefined || b === undefined) return a === b;
  // round to 1 decimal, half away from zero (matches Intl maximumFractionDigits:1)
  const r1 = Math.sign(a) * Math.round(Math.abs(a) * 10);
  const r2 = Math.sign(b) * Math.round(Math.abs(b) * 10);
  return r1 === r2;
};

/**
 * True iff arrays differ at any defined cell (rounding-aware via sameRounded).
 * Used by the editedMeasures lifecycle to decide whether the FE shadow entry
 * needs to persist (override differs from saved state) or can be dropped
 * (matches saved state, falls back to row.measures).
 */
export const hasAnyNetOverride = (
  a: number[] | undefined,
  b: number[] | undefined,
): boolean => {
  if (!a || !b) return false;
  const n = Math.max(a.length, b.length);
  for (let i = 0; i < n; i++) {
    if (a[i] === undefined) continue;
    if (!sameRounded(a[i], b[i])) return true;
  }
  return false;
};

/**
 * Effective saved override for parity-walk eligibility. `suggestParityRun`
 * skips cells whose current value differs from the saved override, treating
 * them as in-session typed edits that the planner doesn't want clobbered.
 * That heuristic mis-classifies X-cleared cells: they're at baseline because
 * the planner is reverting a prior save, not because they typed something
 * new — so they should be eligible for parity-walk reduction the same as
 * never-edited cells. Substituting baseline at cleared positions makes the
 * sameRounded check pass and re-includes those cells in the walk.
 */
export const effectiveSavedForParity = (
  savedOverride: number[],
  baseline: number[],
  clearedForRow: ReadonlySet<number> | undefined,
): number[] => {
  if (!clearedForRow || clearedForRow.size === 0) return savedOverride;
  return savedOverride.map((v, i) =>
    clearedForRow.has(i) ? (baseline[i] ?? v) : v
  );
};
